import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useEntitlements } from '@/hooks/useEntitlements';

interface Props {
  children: JSX.Element;
}

/**
 * Gate for /recruitment/*. Recruiter status comes from the billing snapshot
 * (/api/billing/me → useEntitlements().isRecruiter), the single source the SPA
 * already fetches for gating. Loading placeholders mirror AdminRoute so we
 * never flash a redirect before claims/snapshot settle.
 */
export function RecruiterRoute({ children }: Props): JSX.Element {
  const { t } = useTranslation();
  const { token, status, user } = useAuth();
  const { loaded, isRecruiter } = useEntitlements();
  const location = useLocation();

  if (token && status === 'verifying') {
    return <main className="results-loading" role="status" aria-live="polite">{t('auth.checkingSignIn')}</main>;
  }
  if (!token && status === 'loading' && !user) {
    return <main className="results-loading" role="status" aria-live="polite">{t('auth.settingUpSession')}</main>;
  }
  if (!token && status !== 'authenticated') {
    return <Navigate to="/login" replace state={{ from: { pathname: location.pathname, search: location.search } }} />;
  }

  // Real user, snapshot still loading → placeholder (don't flash a redirect).
  if (!loaded) {
    return <main className="results-loading" role="status" aria-live="polite">{t('common.loading')}</main>;
  }
  if (!isRecruiter) {
    return <Navigate to="/" replace />;
  }
  return children;
}
